import { Form } from "antd"
import { Alert, Checkbox, Legend, RadioButtons, SelectInput, TextInput } from "components/common"
import { t } from "i18next"
import { interestOptions } from "../utils"
import { useLazyGetClientNotInterestedReasonsQuery } from "features/leads/api"
import { useMemo } from "react"
import { mapKeysToValues } from "utils"
import { IconText } from "components/custom"
import theme from "utils/themeVars"
import getPrivilege from "utils/privileges/privileges"
import { useSelector } from "react-redux"

const AuditReviewForm = ({ form, rules, concerns, isFetchingConcerns, lead, isFlaggedInAuditReview }) => {
  const userPrivileges = useSelector(state => state.app.privileges)

  const clientInterest = Form.useWatch("clientInterested", form)
  const clientinterestReasonId = Form.useWatch("clientNotInterestedReasonId", form)
  const clientConcernIds = Form.useWatch("areasOfImprovementsIds", form)
  const watchListCheck = Form.useWatch("watchlist", form)

  const [getClientNotInterestedreasons, { data: reasons, isFetching: isFetchingReasons }] =
    useLazyGetClientNotInterestedReasonsQuery()

  const reasonsKeyMap = useMemo(() => (reasons ? mapKeysToValues(reasons) : {}), [reasons])
  const concernsKeyMap = useMemo(() => (concerns ? mapKeysToValues(concerns, "key", "value") : {}), [concerns])

  const getReviewMessage = () => {
    return isFlaggedInAuditReview && !clientConcernIds?.length
      ? t(
          "Your review selections make the lead no longer meet the flagging criteria and will be removed from the watchlist once submitted."
        )
      : t("This lead is already in the watchlist. Adding the review will not remove it from the watchlist.")
  }

  return (
    <>
      <Legend title={t("Progression")} />
      <Form.Item name="clientInterested" label={t("Client Interest")} required rules={rules}>
        <RadioButtons
          options={interestOptions()}
          getValue={e => e.value}
          onChange={e => !e?.target?.value && getClientNotInterestedreasons(true)}
        />
      </Form.Item>
      {!clientInterest && (
        <>
          <Form.Item name={"clientNotInterestedReasonId"} label={t("Reason")} rules={rules} required>
            <SelectInput
              placeholder={t("Select reason")}
              options={reasons}
              getOptionValue={e => e?.id}
              inputLoading={isFetchingReasons}
              onChange={(value, option) => {
                form.setFieldsValue({ clientNotInterestedReasonId: value })
              }}
            />
          </Form.Item>
          <Form.Item
            name="clientNotInterestedReasonValue"
            label={t("Comment")}
            required={clientinterestReasonId ? reasonsKeyMap?.["other"] === clientinterestReasonId : false}
            rules={clientinterestReasonId ? reasonsKeyMap?.["other"] === clientinterestReasonId && rules : []}
            extra={
              <IconText
                icon="MdInfoOutline"
                iconProps={{ color: theme["gray800"], size: "1em" }}
                textType="secondary"
                title={t("Maximum 40 characters")}
              />
            }
          >
            <TextInput placeholder={t("Enter comment")} maxLength={40} />
          </Form.Item>
        </>
      )}
      <Legend title={t("Areas of Concern")} />
      <Form.Item name={"areasOfImprovementsIds"} label={t("Concerns")} rules={rules}>
        <SelectInput
          placeholder={t("Select Concerns")}
          options={concerns}
          inputLoading={isFetchingConcerns}
          mode="multiple"
          onChange={value => {
            form.setFieldsValue({ watchlist: value?.length ? true : false })
          }}
        />
      </Form.Item>
      {clientConcernIds?.includes(concernsKeyMap?.["other"]) && (
        <Form.Item
          name="otherImprovement"
          label={t("Others")}
          required
          rules={rules}
          extra={
            <IconText
              icon="MdInfoOutline"
              iconProps={{ color: theme["gray800"], size: "1em" }}
              textType="secondary"
              title={t("Maximum 25 characters")}
            />
          }
        >
          <TextInput placeholder={t("Enter Others")} maxLength={25} />
        </Form.Item>
      )}
      <Legend title={t("Overall Feedback")} />
      <Form.Item
        name="feedback"
        label={t("Feedback")}
        rules={rules}
        required
        extra={
          <IconText
            icon="MdInfoOutline"
            iconProps={{ color: theme["gray800"], size: "1em" }}
            textType="secondary"
            title={t("Maximum 500 characters")}
          />
        }
      >
        <TextInput placeholder={t("Enter feedback")} maxLength={500} lineCount={4} style={{ resize: "none" }} />
      </Form.Item>
      {getPrivilege("can_add_to_watchlist", userPrivileges) &&
        (!lead?.isAddedToWatchList || (lead?.isAddedToWatchList && isFlaggedInAuditReview)) && (
          <Form.Item name="watchlist" required rules={rules}>
            <Checkbox
              disabled={lead?.isAddedToWatchList || clientConcernIds?.length}
              checked={watchListCheck}
              onChange={e => form.setFieldValue("watchlist", e?.target?.checked)}
            >
              {t("Add to WatchList")}
            </Checkbox>
          </Form.Item>
        )}

      {lead?.isAddedToWatchList && <Alert type="error" message={getReviewMessage()} className="mbe-8" />}
    </>
  )
}

export default AuditReviewForm
